<script setup>
import { useStationStore } from '@/stores/station'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Station',
  },
})

const router = useRouter()
const ability = useAbility()
const stationStore = useStationStore()

const isLoading = ref(true)
const loadError = ref('')

const loadStations = async () => {
  isLoading.value = true
  loadError.value = ''
  try {
    await stationStore.fetchStations()
  }
  catch (err) {
    loadError.value = err?.data?.message || err?.message || 'Gagal memuat daftar stasiun.'
  }
  finally {
    isLoading.value = false
  }
}

onMounted(loadStations)

const openMonitor = station => {
  if (!station?.id) {
    console.error('❌ Station ID tidak valid:', station)
    return
  }
  
  router.push(`/kds/${station.id}`)
}

const goToManageStations = () => {
  router.push({ name: 'settings-stations' })
}
</script>

<template>
  <div>
    <!-- Page Header -->
    <VRow class="mb-4 align-center">
      <VCol cols="12" md="6">
        <h4 class="text-h4 font-weight-medium mb-1">
          Kitchen Display System (KDS)
        </h4>
        <p class="text-body-2 text-medium-emphasis">
          Pilih stasiun untuk membuka layar monitor dapur/barista secara fullscreen.
        </p>
      </VCol>
      <VCol cols="12" md="6" class="d-flex justify-md-end">
        <VBtn
          v-if="ability.can('update', 'Station')"
          variant="outlined"
          prepend-icon="ri-settings-3-line"
          @click="goToManageStations"
        >
          Kelola Stasiun
        </VBtn>
      </VCol>
    </VRow>

    <!-- Error State -->
    <VAlert v-if="loadError" type="error" variant="tonal" class="mb-4">
      {{ loadError }}
      <template #append>
        <VBtn size="small" variant="text" @click="loadStations">
          Coba Lagi
        </VBtn>
      </template>
    </VAlert>

    <!-- Loading State -->
    <VRow v-if="isLoading">
      <VCol v-for="n in 3" :key="n" cols="12" sm="6" lg="4">
        <VSkeletonLoader type="card" height="180" />
      </VCol>
    </VRow>

    <!-- Empty State -->
    <VAlert
      v-else-if="!stationStore.stations.length"
      type="info"
      variant="tonal"
      class="text-center"
    >
      Belum ada stasiun yang dibuat. Silakan tambahkan stasiun baru terlebih dahulu.
    </VAlert>

    <!-- Station Cards -->
    <VRow v-else>
      <VCol
        v-for="station in stationStore.stations"
        :key="station.id"
        cols="12"
        sm="6"
        lg="4"
      >
        <VCard variant="outlined" class="h-100">
          <VCardItem>
            <template #prepend>
              <VAvatar color="primary" variant="tonal" size="48">
                <VIcon icon="ri-tv-2-line" size="24" />
              </VAvatar>
            </template>
            <VCardTitle class="text-h6">
              {{ station.name }}
            </VCardTitle>
            <VCardSubtitle>
              <VChip size="small" variant="tonal" class="mt-1">
                {{ station.type === 'kitchen' ? 'Dapur' : station.type === 'beverage' ? 'Minuman' : station.type }}
              </VChip>
              <VChip
                v-if="station.is_default"
                size="small"
                color="warning"
                variant="tonal"
                class="mt-1 ml-1"
              >
                Default
              </VChip>
            </VCardSubtitle>
          </VCardItem>

          <VCardText>
            <div class="d-flex align-center gap-2 text-body-2 text-medium-emphasis">
              <VIcon icon="ri-printer-line" size="18" />
              <span>
                {{ station.printer_id ? 'Terhubung ke Printer' : 'Tanpa Printer' }}
              </span>
            </div>
          </VCardText>

          <VCardActions class="pt-0">
            <VBtn
              block
              size="large"
              color="primary"
              prepend-icon="ri-fullscreen-line"
              @click="() => openMonitor(station)"
            >
              Buka Monitor
            </VBtn>
          </VCardActions>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
